import { useMemo, useState } from 'react';
import type {
  HistoryResponse,
  LeagueBundle,
  PlayersFile,
  TransactionItem,
} from '../../shared/types';
import {
  Avatar,
  Empty,
  ErrorState,
  Legend,
  Loading,
  PageHead,
  PositionTag,
  Section,
  Stat,
} from '../components/common';
import { fmtDate, fmtDateTime, fmtPoints, fmtRelative, pickLabel } from '../lib/format';
import { focusWeek } from '../lib/home';
import { buildTeamViews, lookupPlayer, teamIndex, type TeamView } from '../lib/league';
import { managerDirectory, type ManagerRef } from '../lib/rivalries';
import {
  buildPlayerLineages,
  buildProduction,
  buildRosterMaps,
  buildTradeLedger,
  liveWeekRows,
  mergeLiveWeek,
  type Haul,
  type PlayerLineage,
  type TradeAsset,
  type TradeRecord,
  type TradeSide,
} from '../lib/tradetree';
import {
  useAllTransactions,
  useDrafts,
  useLineups,
  useMatchups,
  useTransactions,
} from '../lib/api';
import { Link } from '../lib/router';

type Filter = 'all' | 'trade' | 'waiver' | 'free_agent';

const LABELS: Record<Filter, string> = {
  all: 'All moves',
  trade: 'Trades',
  waiver: 'Waivers',
  free_agent: 'Free agents',
};

const TYPE_PILL: Record<string, string> = {
  trade: 'pill-ember',
  waiver: '',
  free_agent: '',
  commissioner: '',
};

/**
 * What a manager drew from one asset, as a phrase.
 *
 * `scoreable` is the whole reason this is not just a number. An asset acquired
 * after the last week anybody has played has scored nothing AND could not have,
 * so printing "0.00" would report an unplayed rookie as a bust. The two states
 * read completely differently and only one of them is a fact about the trade.
 *
 * The trailing "all" figure counts bench weeks too. It is appended rather than
 * substituted because the two answer different questions: the starter figure is
 * what reached the score and drives every total on the page, while the total is
 * what the PLAYER produced. A wide gap between them is a usage story, not an
 * asset story, and merging them would hide exactly that.
 */
function haulText(haul: Haul, scoreable: boolean): string {
  if (!scoreable) return 'not played yet';

  const started =
    haul.starts === 0
      ? haul.benched > 0 || haul.benchPoints > 0
        ? 'never started'
        : // "No starts" is a claim that the ledger looked and found nothing,
          // which is false for a player whose only week so far has not kicked
          // off. Reachable inside a live week, where a trade is past the
          // horizon while some of its players are not.
          haul.pending > 0
          ? 'not played yet'
          : 'no starts'
      : `${fmtPoints(haul.points)} in ${haul.starts} start${haul.starts === 1 ? '' : 's'}`;

  // Shown whenever he actually SAT, even if those weeks scored nothing.
  //
  // This used to test `benchPoints > 0`, which made sense while a zero might
  // have meant "no data". It does not any more: an unplayed week is now null
  // and counted as `pending`, so a benchPoints of 0 is a measurement -- he
  // played, he was benched, he produced nothing. Suppressing it left Romeo
  // Doubs reading a bare "never started" on a week he had played, which is
  // indistinguishable from the pre-kickoff state this whole split exists to
  // separate. When he never sat there is no second question to answer and the
  // total IS the starter figure, so that case stays silent.
  if (haul.benched === 0) return started;
  const all = Math.round((haul.points + haul.benchPoints) * 100) / 100;
  return `${started} · ${fmtPoints(all)} all`;
}

function ManagerChip({ m, hint }: { m: ManagerRef | undefined; hint?: string }) {
  return (
    <span className="tree-node" title={hint}>
      <Avatar id={m?.avatar ?? null} alt={m?.name ?? '?'} />
      <span className="tree-name">{m?.name ?? 'Unknown'}</span>
    </span>
  );
}

// --- Player trees ----------------------------------------------------------

function PlayerTree({
  lineage,
  dir,
  players,
}: {
  lineage: PlayerLineage;
  dir: Map<string, ManagerRef>;
  players: PlayersFile | null;
}) {
  const player = lookupPlayer(players, lineage.playerId);

  // Whatever the chain does not account for: starts before the first trade, or
  // for a manager who picked him up off waivers inside somebody's window.
  // Printing the career total instead duplicated the hop verbatim whenever one
  // manager accounted for all of it, which is most of them.
  const hopStarts = lineage.hops.reduce((n, h) => n + h.haul.starts, 0);
  const hopPoints = lineage.hops.reduce((n, h) => n + h.haul.points, 0);
  const uncovered = {
    starts: lineage.careerStarts - hopStarts,
    points: Math.round((lineage.careerPoints - hopPoints) * 100) / 100,
  };

  return (
    <article className="card card-pad tree">
      <div className="tree-head">
        <PositionTag position={player.position} />
        <span className="tree-player-name">{player.name}</span>
        <span className="faint tree-count">
          {lineage.hops.length} trade{lineage.hops.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="tree-chain">
        <ManagerChip
          m={lineage.firstOwnerUserId ? dir.get(lineage.firstOwnerUserId) : undefined}
          hint="Held him before the first trade on record"
        />
        {lineage.hops.map((h) => (
          <span key={h.transactionId} className="tree-step">
            <span
              className="tree-arrow"
              title={`${h.tradeSeason} league year, week ${h.week} — ${fmtDate(h.created)}`}
            >
              →
            </span>
            <span className="hop">
              <ManagerChip m={h.toUserId ? dir.get(h.toUserId) : undefined} />
              <span className="hop-haul">{haulText(h.haul, h.scoreable)}</span>
            </span>
          </span>
        ))}
      </div>

      {uncovered.starts > 0 && (
        <div className="tree-outcome">
          <span className="faint">
            {fmtPoints(uncovered.points)} in {uncovered.starts} start
            {uncovered.starts === 1 ? '' : 's'} not covered by a trade above.
          </span>
        </div>
      )}
    </article>
  );
}

// --- Trade ledger ----------------------------------------------------------

function AssetRow({
  asset,
  sideUserId,
  dir,
  players,
}: {
  asset: TradeAsset;
  sideUserId: string | null;
  dir: Map<string, ManagerRef>;
  players: PlayersFile | null;
}) {
  if (asset.kind === 'faab') {
    return (
      <div className="asset">
        <span className="in">+</span>
        <span className="asset-pick">${asset.amount} FAAB</span>
        <span className="asset-pts">not scored</span>
      </div>
    );
  }

  if (asset.kind === 'player') {
    const p = lookupPlayer(players, asset.playerId);
    return (
      <div className="asset">
        <span className="in">+</span>
        <PositionTag position={p.position} />
        <span className="asset-name">{p.name}</span>
        <span className="asset-pts">{haulText(asset.haul, asset.scoreable)}</span>
      </div>
    );
  }

  // "via X" names whose pick it originally was, which is only worth saying when
  // that is somebody else. A manager reacquiring their own pick would otherwise
  // read as "2026 2nd via danozpro" on danozpro's own side.
  const origin =
    asset.originalUserId && asset.originalUserId !== sideUserId
      ? dir.get(asset.originalUserId)
      : undefined;
  const label = pickLabel(asset.season, asset.round);
  const { outcome, became } = asset;
  const drafter = became?.selectedByUserId ? dir.get(became.selectedByUserId) : undefined;

  // Four endings, and collapsing any pair of them misreports the trade:
  // became-this-side's-player, flipped-onward, and the two ways a pick can still
  // be owed. The haul reads `playedSeason`, never the asset's own `scoreable` --
  // a flipped pick is settled for THIS side while its player may not have kicked
  // off, and those are different questions.
  const ending = became?.credited ? (
    <span className="asset-pts">{haulText(became.haul, became.playedSeason)}</span>
  ) : became ? (
    <span className="asset-pts">traded on</span>
  ) : outcome.kind === 'pending' ? (
    <span className="asset-pts">draft not held</span>
  ) : (
    <span className="asset-pts">never used</span>
  );

  return (
    <>
      <div className="asset">
        <span className="in">+</span>
        <span className="asset-pick">{label}</span>
        {origin && <span className="faint asset-via">via {origin.name}</span>}
        {ending}
      </div>
      {/*
        The player goes on its own line for BOTH endings, which is a layout fact
        as much as a semantic one. Inline, a pick row carries a label, a "via",
        a name and a figure, and at 390px that truncated the only part nobody can
        infer -- "Germie B...", "Carso...". Below, it has the full column.

        It also keeps the figure slot honest: every number in that slot means
        "what this side got", so a flipped pick's haul belongs down here next to
        the manager who actually drafted him, never up there.
      */}
      {became && (
        <div className="asset-note">
          became <span className="asset-note-name">{became.name}</span>
          {!became.credited && (
            <>
              {drafter ? ` for ${drafter.name}` : ''} ·{' '}
              {haulText(became.haul, became.playedSeason)}
            </>
          )}
        </div>
      )}
    </>
  );
}

function LedgerSide({
  side,
  dir,
  players,
}: {
  side: TradeSide;
  dir: Map<string, ManagerRef>;
  players: PlayersFile | null;
}) {
  const m = side.userId ? dir.get(side.userId) : undefined;

  // A side whose assets have all been unable to score has no total, only a
  // dash -- the same rule the hauls follow: zero is a result, absent is not.
  // Note a pick they FLIPPED does not count as evidence the 0.00 is real: it
  // was never going to score for them, because they turned it into another
  // trade. Counting it would print a hard zero for a side that did fine.
  const anyScoreable = side.assets.some(
    (a) =>
      (a.kind === 'player' && a.scoreable) ||
      (a.kind === 'pick' && a.became?.credited === true && a.scoreable),
  );

  return (
    <div className="trade-side">
      <div className="trade-side-name">
        <Avatar id={m?.avatar ?? null} alt={m?.name ?? '?'} />
        {m?.name ?? 'Unknown'}
      </div>
      {side.assets.length === 0 && (
        <div className="faint" style={{ fontSize: 12 }}>
          Nothing received
        </div>
      )}
      {side.assets.map((a, i) => (
        <AssetRow key={i} asset={a} sideUserId={side.userId} dir={dir} players={players} />
      ))}
      <div className="side-total">
        <span className="side-total-pts">{anyScoreable ? fmtPoints(side.points) : '—'}</span>
        <span className="faint">to date</span>
        {/*
          The bench figure rides alongside rather than replacing the total: the
          headline is what reached the score and is what `spread` compares, while
          this is what the assets produced regardless of who started them.
        */}
        {anyScoreable && side.benchPoints > 0 && (
          <span className="faint">
            · {fmtPoints(Math.round((side.points + side.benchPoints) * 100) / 100)} all
          </span>
        )}
        {side.pending > 0 && (
          <span className="faint">
            · {side.pending} unresolved
          </span>
        )}
      </div>
    </div>
  );
}

function LedgerRow({
  trade,
  dir,
  players,
}: {
  trade: TradeRecord;
  dir: Map<string, ManagerRef>;
  players: PlayersFile | null;
}) {
  return (
    <article className="txn">
      <div className="txn-head">
        <span className="pill pill-ember">{trade.season}</span>
        <span className="faint" style={{ fontSize: 12 }}>
          Week {trade.week}
        </span>
        {trade.spread !== null && (
          <span className="pill">{fmtPoints(trade.spread)} apart</span>
        )}
        {!trade.complete && (
          <span className="pill" title="A pick or player in this trade has not played yet">
            unresolved
          </span>
        )}
        <span className="txn-date" title={fmtDateTime(trade.created)}>
          {fmtRelative(trade.created)}
        </span>
      </div>
      <div className="trade-sides">
        {trade.sides.map((s) => (
          <LedgerSide key={s.rosterId} side={s} dir={dir} players={players} />
        ))}
      </div>
    </article>
  );
}

// --- Move feed -------------------------------------------------------------

/**
 * Renders one side of a trade: everything a single roster RECEIVED.
 *
 * Sleeper models a trade as flat `adds`/`drops` maps of player_id -> roster_id
 * plus a separate draft_picks array, so "what did team X get" has to be derived
 * by filtering each collection rather than read directly.
 */
function TradeSideCard({
  txn,
  team,
  teams,
  players,
}: {
  txn: TransactionItem;
  team: TeamView;
  teams: Map<number, TeamView>;
  players: PlayersFile | null;
}) {
  const gotPlayers = Object.entries(txn.adds)
    .filter(([, rosterId]) => rosterId === team.roster_id)
    .map(([playerId]) => lookupPlayer(players, playerId));

  const gotPicks = txn.draftPicks.filter((p) => p.toRosterId === team.roster_id);
  const gotFaab = txn.faab.filter((f) => f.to === team.roster_id);

  const empty = gotPlayers.length === 0 && gotPicks.length === 0 && gotFaab.length === 0;

  return (
    <div className="trade-side">
      <div className="trade-side-name">
        <Avatar id={team.avatar} alt={team.name} />
        <Link to={`/teams/${team.roster_id}`}>{team.name}</Link>
      </div>
      {empty && <div className="faint" style={{ fontSize: 12 }}>Nothing received</div>}
      {gotPlayers.map((p) => (
        <div className="asset" key={p.id}>
          <span className="in">+</span>
          <PositionTag position={p.position} />
          <span>{p.name}</span>
        </div>
      ))}
      {gotPicks.map((p) => {
        const from = teams.get(p.originalRosterId);
        return (
          <div className="asset" key={`${p.season}-${p.round}-${p.originalRosterId}`}>
            <span className="in">+</span>
            <span className="asset-pick">{pickLabel(p.season, p.round)}</span>
            {p.originalRosterId !== team.roster_id && (
              <span className="faint" style={{ fontSize: 11 }}>
                via {from?.name ?? `roster ${p.originalRosterId}`}
              </span>
            )}
          </div>
        );
      })}
      {gotFaab.map((f, i) => (
        <div className="asset" key={`faab-${i}`}>
          <span className="in">+</span>
          <span className="asset-pick">${f.amount} FAAB</span>
        </div>
      ))}
    </div>
  );
}

function MoveRow({
  txn,
  teams,
  players,
}: {
  txn: TransactionItem;
  teams: Map<number, TeamView>;
  players: PlayersFile | null;
}) {
  const team = teams.get(txn.rosterIds[0]);
  const adds = Object.keys(txn.adds).map((id) => lookupPlayer(players, id));
  const drops = Object.keys(txn.drops).map((id) => lookupPlayer(players, id));

  return (
    <div>
      <div className="trade-side-name">
        {team && <Avatar id={team.avatar} alt={team.name} />}
        {team ? <Link to={`/teams/${team.roster_id}`}>{team.name}</Link> : 'League'}
        {txn.bid != null && txn.bid > 0 && (
          <span className="pill" style={{ marginLeft: 6 }}>
            ${txn.bid} bid
          </span>
        )}
      </div>
      {adds.map((p) => (
        <div className="asset" key={`a-${p.id}`}>
          <span className="in">+</span>
          <PositionTag position={p.position} />
          <span>{p.name}</span>
        </div>
      ))}
      {drops.map((p) => (
        <div className="asset" key={`d-${p.id}`}>
          <span className="bad" style={{ fontWeight: 700 }}>
            −
          </span>
          <PositionTag position={p.position} />
          <span style={{ textDecoration: 'line-through' }}>{p.name}</span>
        </div>
      ))}
    </div>
  );
}

// --- Page ------------------------------------------------------------------

type Sort = 'recent' | 'gap';

export default function Transactions({
  bundle,
  history,
  players,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
  players: PlayersFile | null;
}) {
  const feed = useTransactions();
  const [filter, setFilter] = useState<Filter>('trade');
  const [sort, setSort] = useState<Sort>('recent');
  const [season, setSeason] = useState<string | 'all'>('all');
  const [multiOnly, setMultiOnly] = useState(false);

  const teams = useMemo(() => teamIndex(buildTeamViews(bundle)), [bundle]);

  // The ledger spans every league year, which is one transactions request each:
  // the Worker serves a single season per call to stay inside its subrequest
  // budget, so the fan-out belongs here. The feed above is deliberately still
  // this season only -- it is a log, not an archive.
  const leagueIds = useMemo(() => {
    const ids = [bundle.league.league_id];
    for (const s of history?.seasons ?? []) ids.push(s.league_id);
    return ids;
  }, [bundle.league.league_id, history]);

  const txns = useAllTransactions(leagueIds);
  const drafts = useDrafts();
  const lineups = useLineups();
  const dir = useMemo(() => managerDirectory(bundle, history), [bundle, history]);

  /**
   * The week in progress, overlaid on the nightly ledger.
   *
   * `lineups.json` is rebuilt once a night, so without this a Sunday
   * afternoon's points do not reach this page until Monday morning. Gated on
   * `played` -- the live-scoreboard flag, "has scoring started" -- because
   * Sleeper returns a FUTURE week's rows carrying the roster's CURRENT lineup,
   * so overlaying an unstarted week would file today's roster against a week
   * nobody has played.
   */
  const week = useMemo(() => focusWeek(bundle), [bundle]);
  const live = useMatchups(bundle.league.league_id, week);

  const production = useMemo(() => {
    const nightly = buildProduction(lineups.data);
    if (!live.data?.played || !players) return nightly;
    const owners = new Map(bundle.teams.map((t) => [t.roster_id, t.owner_id]));
    return mergeLiveWeek(
      nightly,
      liveWeekRows(
        live.data,
        (rosterId) => owners.get(rosterId) ?? null,
        // A team defence's id already IS its NFL abbreviation; everyone else
        // is resolved through the committed dictionary, whose third field is
        // the team. A player it has dropped resolves to null and is treated as
        // not yet played, which withholds rather than inventing.
        (playerId) => players.players[playerId]?.[2] ?? (playerId.length <= 3 ? playerId : null),
      ),
    );
  }, [lineups.data, live.data, players, bundle.teams]);

  const lineages = useMemo(
    () => (txns.data ? buildPlayerLineages(txns.data, production) : []),
    [txns.data, production],
  );

  const ledger = useMemo(() => {
    if (!txns.data) return [];
    return buildTradeLedger(
      txns.data,
      drafts.data,
      buildRosterMaps(bundle, history, txns.data),
      production,
      lineages,
    );
  }, [txns.data, drafts.data, bundle, history, production, lineages]);

  const seasons = useMemo(
    () => [...new Set(ledger.map((t) => t.season))].sort((a, b) => Number(b) - Number(a)),
    [ledger],
  );

  const shownLedger = useMemo(() => {
    const rows = season === 'all' ? ledger : ledger.filter((t) => t.season === season);
    if (sort === 'recent') return rows;
    // Unresolved trades have no gap to sort by and sink to the bottom rather
    // than sorting as zero, which would read as "these were even".
    return [...rows].sort(
      (a, b) => (b.spread ?? -1) - (a.spread ?? -1) || b.created - a.created,
    );
  }, [ledger, season, sort]);

  const shownTrees = useMemo(
    () => (multiOnly ? lineages.filter((l) => l.hops.length > 1) : lineages),
    [lineages, multiOnly],
  );

  const filtered = useMemo(() => {
    if (!feed.data) return [];
    return filter === 'all'
      ? feed.data.transactions
      : feed.data.transactions.filter((t) => t.type === filter);
  }, [feed.data, filter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: feed.data?.transactions.length ?? 0 };
    for (const t of feed.data?.transactions ?? []) c[t.type] = (c[t.type] ?? 0) + 1;
    return c;
  }, [feed.data]);

  const loading = txns.loading || drafts.loading || lineups.loading;
  const multi = lineages.filter((l) => l.hops.length > 1).length;
  const unresolved = ledger.filter((t) => !t.complete).length;

  return (
    <>
      <PageHead
        title="Transactions"
        meta={[
          `${ledger.length} trades`,
          `${leagueIds.length} league year${leagueIds.length === 1 ? '' : 's'}`,
          'Offseason trades file under week 1',
          'Points are realized starter totals to date',
        ]}
      />

      {!loading && !txns.error && ledger.length > 0 && (
        <div className="grid grid-4" style={{ marginBottom: 20 }}>
          <Stat label="Trades" value={ledger.length} note={`${unresolved} still unresolved`} />
          <Stat
            label="Players moved"
            value={lineages.length}
            note={`${multi} traded more than once`}
          />
          <Stat
            label="Picks moved"
            value={ledger.reduce(
              (n, t) => n + t.sides.reduce((m, s) => m + s.assets.filter((a) => a.kind === 'pick').length, 0),
              0,
            )}
          />
          <Stat
            label="Points credited"
            value={fmtPoints(
              ledger.reduce((n, t) => n + t.sides.reduce((m, s) => m + s.points, 0), 0),
            )}
            note="Started for whoever acquired them"
          />
        </div>
      )}

      <Section title="Trade Ledger" hint="What each side has scored from it since">
        {loading && <Loading label="Following every trade" />}
        {txns.error && <ErrorState message={txns.error} onRetry={txns.reload} />}

        {!loading && !txns.error && (
          <>
            <div className="toolbar">
              <button
                className={`chip ${sort === 'recent' ? 'on' : ''}`}
                onClick={() => setSort('recent')}
              >
                Newest
              </button>
              <button
                className={`chip ${sort === 'gap' ? 'on' : ''}`}
                onClick={() => setSort('gap')}
              >
                Widest gap
              </button>
              <button
                className={`chip ${season === 'all' ? 'on' : ''}`}
                onClick={() => setSeason('all')}
              >
                All years
              </button>
              {seasons.map((s) => (
                <button
                  key={s}
                  className={`chip ${season === s ? 'on' : ''}`}
                  onClick={() => setSeason(s)}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="card">
              {shownLedger.length === 0 ? (
                <Empty>No trades recorded yet.</Empty>
              ) : (
                shownLedger.map((t) => (
                  <LedgerRow key={t.transactionId} trade={t} dir={dir} players={players} />
                ))
              )}
            </div>

            <Legend
              items={[
                { term: 'To date', def: 'points scored while started, since the trade' },
                { term: 'All', def: 'the same points counting bench weeks too' },
                { term: 'Not played yet', def: 'acquired after the last week anyone has played' },
                { term: 'Unresolved', def: 'a pick or player here has not played yet' },
                { term: 'Traded on', def: 'the pick moved again; its player is still named' },
                { term: 'Apart', def: 'gap between the two sides, once both have played' },
              ]}
            />
          </>
        )}
      </Section>

      <Section title="Player Trees" hint="Every player who has changed hands">
        {loading && <Loading label="Following every traded player" />}

        {!loading && !txns.error && (
          <>
            <div className="toolbar">
              <button className={`chip ${multiOnly ? '' : 'on'}`} onClick={() => setMultiOnly(false)}>
                All players
                <span className="faint"> {lineages.length}</span>
              </button>
              <button className={`chip ${multiOnly ? 'on' : ''}`} onClick={() => setMultiOnly(true)}>
                Traded more than once
                <span className="faint"> {multi}</span>
              </button>
            </div>

            {shownTrees.length === 0 ? (
              <div className="card">
                <Empty>No players have been traded yet.</Empty>
              </div>
            ) : (
              <div className="grid grid-2 grid-start">
                {shownTrees.map((l) => (
                  <PlayerTree key={l.playerId} lineage={l} dir={dir} players={players} />
                ))}
              </div>
            )}
          </>
        )}
      </Section>

      <Section title="Move Feed" hint={`${bundle.league.season} league year, newest first`}>
        <div className="toolbar">
          {(Object.keys(LABELS) as Filter[]).map((f) => (
            <button
              key={f}
              className={`chip ${filter === f ? 'on' : ''}`}
              onClick={() => setFilter(f)}
            >
              {LABELS[f]}
              {counts[f] != null && <span className="faint"> {counts[f]}</span>}
            </button>
          ))}
        </div>

        {feed.loading && <Loading label="Loading transactions" />}
        {feed.error && <ErrorState message={feed.error} onRetry={feed.reload} />}

        {feed.data && (
          <div className="card">
            {filtered.length === 0 ? (
              <Empty>No {LABELS[filter].toLowerCase()} recorded yet.</Empty>
            ) : (
              filtered.map((txn) => (
                <article className="txn" key={txn.transaction_id}>
                  <div className="txn-head">
                    <span className={`pill ${TYPE_PILL[txn.type] ?? ''}`}>
                      {txn.type.replace('_', ' ')}
                    </span>
                    <span className="faint" style={{ fontSize: 12 }}>
                      Week {txn.week}
                    </span>
                    <span className="txn-date" title={fmtDateTime(txn.created)}>
                      {fmtRelative(txn.created)}
                    </span>
                  </div>

                  {txn.type === 'trade' ? (
                    <div className="trade-sides">
                      {txn.rosterIds.map((rid) => {
                        const team = teams.get(rid);
                        return team ? (
                          <TradeSideCard
                            key={rid}
                            txn={txn}
                            team={team}
                            teams={teams}
                            players={players}
                          />
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <MoveRow txn={txn} teams={teams} players={players} />
                  )}
                </article>
              ))
            )}
          </div>
        )}
      </Section>
    </>
  );
}
